import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Calendar, Send, Building2, DollarSign, Cpu, Globe, Users, FileText, Sparkles, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface NewsArticle {
  id: number;
  source_id: number;
  title: string;
  summary?: string;
  content?: string;
  category?: string;
  publish_time?: string;
  status: number;
  created_at: string;
  updated_at: string;
  source_name?: string;
}

interface NewsSource {
  id: number;
  name: string;
  type?: string;
}

interface FormData {
  source_id: number;
  title: string;
  summary: string;
  content: string;
  category: string;
  publish_time: string;
  status: number;
  original_url: string;
}

const NewsArticleManagement: React.FC = () => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10); // 每页显示10条
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState<NewsArticle | null>(null);
  const [selectedArticles, setSelectedArticles] = useState<number[]>([]);
  const [batchPublishing, setBatchPublishing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    source_id: 0,
    title: '',
    summary: '',
    content: '',
    category: '',
    publish_time: '',
    status: 0,
    original_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  const categories = [
    '政治', '经济', '科技', '社会', '文化', '体育', '娱乐', '国际', '军事', '教育'
  ];

  useEffect(() => {
    fetchArticles();
    fetchSources();
  }, []);

  // 防抖搜索
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);
  
  // 当页码改变时立即获取数据
  useEffect(() => {
    fetchArticles();
  }, [currentPage]);

  // 当搜索条件或筛选条件改变时，重置到第一页并防抖获取数据
  useEffect(() => {
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    const timeout = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchArticles();
      }
    }, 500); // 500ms防抖
    
    setSearchDebounce(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm, statusFilter, sourceFilter]);

  const fetchSources = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=sources&action=list&status=1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 确保 result.data 是数组，如果有分页结构则取 data.data
        const sourcesData = result.data?.data || result.data || [];
        setSources(Array.isArray(sourcesData) ? sourcesData : []);
      } else {
        console.error('获取新闻源失败:', result.error);
        setSources([]); // 确保设置为空数组
      }
    } catch (error) {
      console.error('获取新闻源失败:', error);
      setSources([]); // 确保设置为空数组
    }
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      
      // 构建请求体，包含分页和筛选参数
      const requestBody: any = {
        page: currentPage,
        limit: pageSize
      };
      
      // 添加搜索和筛选参数
      if (searchTerm) {
        requestBody.search = searchTerm;
      }
      if (statusFilter !== 'all') {
        requestBody.status = statusFilter;
      }
      if (sourceFilter !== 'all') {
        requestBody.source_id = sourceFilter;
      }

      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=articles&action=list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 映射数据结构
        const mappedArticles = (result.data?.data || result.data || []).map((article: any) => ({
          ...article,
          source_name: article.insight_news_source?.name
        }));
        setArticles(mappedArticles);
        
        // 设置分页信息
        if (result.data?.pagination) {
          setTotalPages(result.data.pagination.totalPages || 1);
          setTotalCount(result.data.pagination.totalCount || 0);
        } else {
          // 如果没有分页信息，假设只有一页
          setTotalPages(1);
          setTotalCount(mappedArticles.length);
        }
      } else {
        console.error('获取新闻文章失败:', result.error);
        setArticles([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('获取新闻文章失败:', error);
      setArticles([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      
      if (editingArticle) {
        // 更新文章
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=articles&action=update`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: editingArticle.id.toString(),
            source_id: formData.source_id.toString(),
            title: formData.title,
            summary: formData.summary,
            content: formData.content,
            category: formData.category,
            publish_time: formData.publish_time,
            status: formData.status,
            original_url: formData.original_url
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          console.error('更新新闻文章失败:', result.error);
          alert('更新失败：' + result.error);
          return;
        }
      } else {
        // 创建文章
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=articles&action=create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            source_id: formData.source_id.toString(),
            title: formData.title,
            summary: formData.summary,
            content: formData.content,
            category: formData.category,
            publish_time: formData.publish_time,
            status: formData.status,
            original_url: formData.original_url
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          console.error('创建新闻文章失败:', result.error);
          alert('创建失败：' + result.error);
          return;
        }
      }

      await fetchArticles();
      setShowForm(false);
      setEditingArticle(null);
      resetForm();
    } catch (error) {
      console.error('保存新闻文章失败:', error);
      alert('保存失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这篇文章吗？')) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=articles&action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        console.error('删除文章失败:', result.error);
        alert('删除失败：' + result.error);
        return;
      }

      await fetchArticles();
    } catch (error) {
      console.error('删除文章失败:', error);
      alert('删除失败，请重试');
    }
  };

  // 发布文章
  const handlePublish = async (article: NewsArticle) => {
    if (!confirm(`确定要发布文章「${article.title}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-articles-admin-manage?action=update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: article.id.toString(),
          status: 1,
          publish_time: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        console.error('发布文章失败:', result.error);
        alert('发布失败：' + result.error);
        return;
      }

      alert('文章发布成功！');
      await fetchArticles();
    } catch (error) {
      console.error('发布文章失败:', error);
      alert('发布失败，请重试');
    }
  };

  // 取消发布（设为草稿）
  const handleUnpublish = async (article: NewsArticle) => {
    if (!confirm(`确定要将文章「${article.title}」设为草稿吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-articles-admin-manage?action=update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: article.id.toString(),
          status: 0
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        console.error('取消发布失败:', result.error);
        alert('操作失败：' + result.error);
        return;
      }

      alert('文章已设为草稿！');
      await fetchArticles();
    } catch (error) {
      console.error('取消发布失败:', error);
      alert('操作失败，请重试');
    }
  };

  // 批量发布文章
  const handleBatchPublish = async () => {
    if (selectedArticles.length === 0) {
      alert('请先选择要发布的文章');
      return;
    }

    const selectedDrafts = articles.filter(article =>
      selectedArticles.includes(article.id) && article.status === 0
    );

    if (selectedDrafts.length === 0) {
      alert('所选文章中没有草稿状态的文章');
      return;
    }

    if (!confirm(`确定要发布 ${selectedDrafts.length} 篇文章吗？`)) return;

    try {
      setBatchPublishing(true);
      
      // 逐个发布文章
      for (const article of selectedDrafts) {
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=articles&action=update`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: article.id.toString(),
            status: 1,
            publish_time: new Date().toISOString()
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          console.error('批量发布失败:', result.error);
          alert('批量发布失败：' + result.error);
          return;
        }
      }
      
      alert(`成功发布 ${selectedDrafts.length} 篇文章！`);
      setSelectedArticles([]);
      await fetchArticles();
    } catch (error) {
      console.error('批量发布失败:', error);
      alert('批量发布失败，请重试');
    } finally {
      setBatchPublishing(false);
    }
  };

  // 选择/取消选择文章
  const handleSelectArticle = (articleId: number) => {
    setSelectedArticles(prev => 
      prev.includes(articleId) 
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    const draftArticles = filteredArticles.filter(article => article.status === 0);
    if (selectedArticles.length === draftArticles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(draftArticles.map(article => article.id));
    }
  };

  const handleEdit = (article: NewsArticle) => {
    setEditingArticle(article);
    setFormData({
      source_id: article.source_id,
      title: article.title,
      summary: article.summary || '',
      content: article.content || '',
      category: article.category || '',
      publish_time: article.publish_time ? article.publish_time.split('T')[0] : '',
      status: article.status,
      original_url: (article as any).original_url || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      source_id: 0,
      title: '',
      summary: '',
      content: '',
      category: '',
      publish_time: '',
      status: 0,
      original_url: ''
    });
  };

  // AI生成摘要
  const generateSummary = async () => {
    if (!formData.title && !formData.content) {
      alert('请先输入标题或内容');
      return;
    }

    setGeneratingSummary(true);
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/generate-article-summary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          content: formData.content
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFormData({ ...formData, summary: data.summary });
        } else {
          alert(data.error || '生成摘要失败');
        }
      } else {
        alert('生成摘要失败，请稍后重试');
      }
    } catch (error) {
      console.error('生成摘要错误:', error);
      alert('生成摘要失败，请检查网络连接');
    } finally {
      setGeneratingSummary(false);
    }
  };

  // 由于现在在后端进行筛选，这里直接使用articles数据
  const filteredArticles = articles;

  const getSourceName = (sourceId: number) => {
    if (!Array.isArray(sources)) return '未知来源';
    return sources.find(s => s.id === sourceId)?.name || '未知来源';
  };

  const getSourceTypeIcon = (type: string) => {
    switch (type) {
      case 'official': return <Building2 className="w-4 h-4 text-red-600" />;
      case 'finance': return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'tech': return <Cpu className="w-4 h-4 text-blue-600" />;
      case 'international': return <Globe className="w-4 h-4 text-purple-600" />;
      case 'youth': return <Users className="w-4 h-4 text-orange-600" />;
      case 'comprehensive': return <FileText className="w-4 h-4 text-gray-600" />;
      default: return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSourceInfo = (sourceId: number) => {
    if (!Array.isArray(sources)) {
      return {
        name: '未知来源',
        type: 'comprehensive'
      };
    }
    const source = sources.find(s => s.id === sourceId);
    return {
      name: source?.name || '未知来源',
      type: source?.type || 'comprehensive'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">新闻文章管理</h2>
        </div>
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索文章标题..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="1">已发布</option>
              <option value="0">草稿</option>
            </select>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部来源</option>
              {Array.isArray(sources) && sources.map(source => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-3">
              {selectedArticles.length > 0 && (
                <button
                  onClick={handleBatchPublish}
                  disabled={batchPublishing}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {batchPublishing ? '发布中...' : `批量发布 (${selectedArticles.length})`}
                </button>
              )}
              <button
                onClick={() => {
                  setEditingArticle(null);
                  resetForm();
                  setShowForm(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                添加文章
              </button>
            </div>
          </div>

          {/* 文章列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedArticles.length === filteredArticles.filter(article => article.status === 0).length && filteredArticles.filter(article => article.status === 0).length > 0}
                        onChange={handleSelectAll}
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      标题
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    来源
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分类
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    发布时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="flex items-start">
                        {article.status === 0 && (
                          <input
                            type="checkbox"
                            checked={selectedArticles.includes(article.id)}
                            onChange={() => handleSelectArticle(article.id)}
                            className="mr-3 mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        )}
                        <div className={article.status === 0 ? '' : 'ml-7'}>
                          <div className="max-w-xs truncate">{article.title}</div>
                          {article.summary && (
                            <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                              {article.summary}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        const sourceInfo = getSourceInfo(article.source_id);
                        return (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {getSourceTypeIcon(sourceInfo.type)}
                            <span className="ml-1">{sourceInfo.name}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {article.category ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {article.category}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        article.status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {article.status === 1 ? (
                          <><Eye className="w-3 h-3 mr-1" />已发布</>
                        ) : (
                          <><EyeOff className="w-3 h-3 mr-1" />草稿</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {article.publish_time ? new Date(article.publish_time).toLocaleDateString() : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {article.status === 0 ? (
                          <button
                            onClick={() => handlePublish(article)}
                            className="text-green-600 hover:text-green-900 flex items-center"
                            title="发布文章"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnpublish(article)}
                            className="text-orange-600 hover:text-orange-900 flex items-center"
                            title="设为草稿"
                          >
                            <EyeOff className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(article)}
                          className="text-blue-600 hover:text-blue-900"
                          title="编辑文章"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="text-red-600 hover:text-red-900"
                          title="删除文章"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分页组件 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  显示第 {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} 条，
                  共 {totalCount} 条记录
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>
                
                {/* 页码按钮 */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </div>
            </div>
          )}

          {filteredArticles.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">暂无新闻文章数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingArticle ? '编辑新闻文章' : '添加新闻文章'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      新闻源 *
                    </label>
                    <select
                      required
                      value={formData.source_id}
                      onChange={(e) => setFormData({ ...formData, source_id: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={0}>请选择新闻源</option>
                      {Array.isArray(sources) && sources.map(source => (
                        <option key={source.id} value={source.id}>
                          {source.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      分类
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">请选择分类</option>
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    标题 *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入文章标题"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      摘要
                    </label>
                    <button
                      type="button"
                      onClick={generateSummary}
                      disabled={generatingSummary || (!formData.title && !formData.content)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-sm hover:shadow-md"
                      title="根据标题和内容智能生成摘要"
                    >
                      {generatingSummary ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          <span className="font-semibold">AI生成中...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3 mr-1.5" />
                          <span className="font-semibold">AI生成摘要</span>
                        </>
                      )}
                    </button>
                  </div>
                  <textarea
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder="请输入文章摘要，或点击上方按钮AI智能生成"
                  />
                  {formData.summary && (
                    <div className="mt-2 text-xs text-gray-500">
                      摘要长度: {formData.summary.length} 字符
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    内容
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入文章内容"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    原文链接
                  </label>
                  <input
                    type="url"
                    value={formData.original_url}
                    onChange={(e) => setFormData({ ...formData, original_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入新闻原文链接"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      发布时间
                    </label>
                    <input
                      type="date"
                      value={formData.publish_time}
                      onChange={(e) => setFormData({ ...formData, publish_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      状态
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={0}>草稿</option>
                      <option value={1}>已发布</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingArticle(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {submitting ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsArticleManagement;